import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

/**
 * Reusable collapsible wrapper for the big forms on the Partner Portal.
 * Default collapsed so the tab renders calm on first load — the header
 * still shows title + a one-line hint so partners know what's inside.
 *
 * Props:
 *  - title (string, required) — bold header text
 *  - hint (string, optional) — subtle helper below the title
 *  - defaultOpen (bool, default false)
 *  - testid (string, optional) — root data-testid; toggle button gets `${testid}-toggle`
 *  - icon (lucide icon component, optional)
 *  - children — the form JSX
 */
export default function CollapsibleForm({ title, hint, defaultOpen = false, testid, icon: Icon, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="aii-card overflow-hidden" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid={testid ? `${testid}-toggle` : undefined}
        className="w-full text-left p-4 flex items-center justify-between gap-3 hover:bg-[color:var(--aii-green-tint)]/60 transition"
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {open ? <ChevronDown size={14} className="text-brand flex-shrink-0"/> : <ChevronRight size={14} className="text-brand flex-shrink-0"/>}
          {Icon && <Icon size={14} className="text-brand flex-shrink-0"/>}
          <div className="min-w-0">
            <div className="font-display font-semibold text-sm">{title}</div>
            {hint && <div className="text-[11px] text-[color:var(--aii-muted)] mt-0.5">{hint}</div>}
          </div>
        </div>
        <span className="text-[10px] uppercase tracking-widest font-bold text-[color:var(--aii-muted)] flex-shrink-0">
          {open ? "Hide" : "Open"}
        </span>
      </button>
      {open && (
        <div className="border-t border-[color:var(--aii-border)] p-5 space-y-3">
          {children}
        </div>
      )}
    </div>
  );
}
